import { useCallback, useEffect, useRef } from "react";
import { useOrderFlow } from "../app/providers/OrderFlowProvider";
import { useNavigate } from "react-router-dom";
import { formatDatetimeByLocale } from "../helpers/normalizers";
import { useTranslation } from "react-i18next";

type BrowserNotificationOrderData = CashBoxOrderSummary | CashBoxOrderDetails;
type BrowserNotificationPayload = {
    title: string;
    options: NotificationOptions;
};

const getErrorInfo = (error: unknown) => {
    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
        };
    }

    return {
        name: undefined,
        message: typeof error === "string" ? error : undefined,
    };
};

const useBrowserOrderNotification = () => {
    const { i18n } = useTranslation();

    const navigate = useNavigate();

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const notificationPermissionRequestedRef = useRef(false);
    const audioUnlockedRef = useRef(false);

    const {
        openOrderByOrderDetails,
    } = useOrderFlow();


    useEffect(() => {
        audioRef.current = new Audio("/sounds/notification-ding-dong.mp3");
        audioRef.current.preload = "auto";
    }, []);

    const requestNotificationPermissionFromGesture = useCallback(() => {
        if (!("Notification" in window)) return;
        if (Notification.permission !== "default") return;
        if (notificationPermissionRequestedRef.current) return;

        notificationPermissionRequestedRef.current = true;

        const permissionRequest = Notification.requestPermission();
        if (permissionRequest && typeof permissionRequest.catch === "function") {
            permissionRequest.catch((e) => {
                console.warn("Notification permission request failed", {
                    name: e?.name,
                    message: e?.message,
                });
            });
        }
    }, []);

    const unlockNotificationAudioFromGesture = useCallback(() => {
        if (!audioRef.current || audioUnlockedRef.current) return;

        const audio = audioRef.current;
        const previousVolume = audio.volume;
        audio.volume = 0;

        const unlockPlayPromise = audio.play();
        if (unlockPlayPromise && typeof unlockPlayPromise.then === "function") {
            unlockPlayPromise.then(() => {
                audio.pause();
                audio.currentTime = 0;
                audio.volume = previousVolume;
                audioUnlockedRef.current = true;
            })
            .catch((e) => {
                audio.volume = previousVolume;
                console.warn("Audio unlock failed", {
                    name: e?.name,
                    message: e?.message,
                    permission: "Notification" in window ? Notification.permission : "unsupported",
                    visibility: document.visibilityState,
                });
            });
            return;
        }

        audio.pause();
        audio.currentTime = 0;
        audio.volume = previousVolume;
        audioUnlockedRef.current = true;
    }, []);

    useEffect(() => {
        const onFirstUserGesture = () => {
            requestNotificationPermissionFromGesture();
            unlockNotificationAudioFromGesture();
        };

        const eventNames = ["pointerdown", "touchstart", "keydown", "click"];
        eventNames.forEach((eventName) => {
            document.addEventListener(eventName, onFirstUserGesture, { passive: true });
        });

        return () => {
            eventNames.forEach((eventName) => {
                document.removeEventListener(eventName, onFirstUserGesture);
            });
        };
    }, [requestNotificationPermissionFromGesture, unlockNotificationAudioFromGesture]);


    const ensureMainRoute = useCallback(() => {
        if (window.location.pathname !== "/") {
            const currSP_str = new URLSearchParams(window.location.search).toString();
            navigate(`/${currSP_str ? "?" + currSP_str : ""}`, { replace: true });
            return false;
        }
        return true;
    }, [navigate]);

    // const openOrderFromNotification = ({
    const openOrderIfOnAnotherRoute = ({
        orderDetails
    }: {
        orderDetails: BrowserNotificationOrderData;
    }) => {
        const isOnMain = ensureMainRoute();

        if (!isOnMain) {
            setTimeout(() => {
                openOrderByOrderDetails({ orderDetails });
            }, 0);
            return;
        }

        openOrderByOrderDetails({ orderDetails });
    };

    const buildOrderNotificationPayload = useCallback((orderDetails: BrowserNotificationOrderData): BrowserNotificationPayload => {
        const creationDate = formatDatetimeByLocale(
            orderDetails?.created_at_timezone,
            i18n,
            { fallback: "- - -" }
        );

        const searchParams = new URLSearchParams();
        if (orderDetails?.table_id) {
            searchParams.set("tableId", `${orderDetails.table_id}`);
        }
        if (orderDetails?.order_type) {
            searchParams.set("orderType", `${orderDetails.order_type}`);
        }
        if (orderDetails?.id) {
            searchParams.set("orderId", `${orderDetails.id}`);
        }

        return {
            title: ` Order: #${orderDetails.id}`,
            options: {
                body: `Total: ${orderDetails.cost}\nType: ${orderDetails.order_type}\nUpdated at: ${creationDate}`,
                tag: `order-${orderDetails.id}`,
                icon: "/app_icon_152x152.png",
                data: {
                    url: `/${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
                },
            },
        };
    }, [i18n]);

    const showBrowserNotification = useCallback(async (orderDetails: BrowserNotificationOrderData) => {
        if (!("Notification" in window)) return;
        if (Notification.permission !== "granted") return;

        const payload = buildOrderNotificationPayload(orderDetails);

        if ("serviceWorker" in navigator) {
            try {
                const registration = await navigator.serviceWorker.ready;
                if (typeof registration.showNotification === "function") {
                    await registration.showNotification(payload.title, payload.options);
                    return;
                }
            } catch (error) {
                console.warn("Service worker notification failed", {
                    orderId: orderDetails?.id,
                    ...getErrorInfo(error),
                });
            }
        }

        try {
            const notification = new Notification(payload.title, payload.options);

            notification.onclick = () => {
                if (!orderDetails.id) return;

                openOrderIfOnAnotherRoute({
                    orderDetails,
                });

                window.focus();
            };
        } catch (error) {
            console.warn("Page notification failed", {
                orderId: orderDetails?.id,
                ...getErrorInfo(error),
            });
        }
    }, [buildOrderNotificationPayload, openOrderIfOnAnotherRoute]);

    const OrderNotificationBrowserAlert = (order_det?: BrowserNotificationOrderData | null) => {
        if (!order_det) return;

        // Play sound
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            const playPromise = audioRef.current.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch((e) => {
                    console.warn("Notification audio play failed", {
                        orderId: order_det?.id,
                        name: e?.name,
                        message: e?.message,
                        permission: "Notification" in window ? Notification.permission : "unsupported",
                        visibility: document.visibilityState,
                        audioUnlocked: audioUnlockedRef.current,
                    });
                });
            }
        }

        void showBrowserNotification(order_det);
    };


    return {OrderNotificationBrowserAlert};
};

export default useBrowserOrderNotification;
